import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, CalendarCheck, Video, ShieldQuestion } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import BotanicalArt from "@/components/BotanicalArt";

const STEPS = [
  {
    n: "01",
    icon: MessageCircle,
    title: "Say hello",
    text: "Everything starts with a simple WhatsApp message to Eunoia. You don't need the right words — \"hi, I'd like to talk to someone\" is enough. A real person replies, not a bot.",
    q: "What if I don't know what to say?",
    a: "Most people don't. We'll gently ask what brought you here and help you find a therapist who fits. No forms, no phone calls unless you want one.",
  },
  {
    n: "02",
    icon: CalendarCheck,
    title: "Choose your person & your time",
    text: "Browse our therapists, read how they work and who they tend to click with, then pick a slot that suits your life — mornings before work, late evenings, weekends. Your first session starts at ₹99.",
    q: "What if I pick the wrong therapist?",
    a: "There's no wrong pick. If the fit doesn't feel right after a session, you can switch — free, no questions, no awkwardness. Finding your person is part of the process.",
  },
  {
    n: "03",
    icon: Video,
    title: "Talk — from wherever feels safe",
    text: "Your session is a secure, confidential video (or audio) call — 30 to 60 minutes. Your room, your car, a quiet corner — wherever you feel most at ease.",
    q: "Is it really private?",
    a: "Yes. We don't store your chats or keep session histories. What you say stays between you and your psychologist. Nothing appears on any family or work account.",
  },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <section className="mx-auto max-w-screen-2xl px-6 py-16 md:px-12 md:py-24 lg:px-24">
        <Reveal>
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-accent">
            <span className="h-px w-10 bg-accent" /> How therapy works
          </p>
          <h1 className="mt-6 max-w-4xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-6xl">
            Therapy, explained without the intimidation.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            A therapy session is simply a conversation with someone trained to listen —
            really listen — and help you make sense of what you're carrying. Here's the whole
            journey, start to finish.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-screen-2xl px-6 py-20 md:px-12 md:py-28 lg:px-24">
        <div className="space-y-20">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={0.05}>
              <div className="grid gap-10 border-t border-border pt-12 md:grid-cols-12" data-testid={`how-step-${s.n}`}>
                <div className="md:col-span-2">
                  <span className="font-serif text-7xl font-medium italic text-primary/15">{s.n}</span>
                </div>
                <div className="md:col-span-5">
                  <s.icon className="h-7 w-7 text-accent" />
                  <h2 className="mt-5 font-serif text-3xl font-bold tracking-tight md:text-4xl">{s.title}</h2>
                  <p className="mt-5 text-sm leading-relaxed text-muted-foreground md:text-base">{s.text}</p>
                </div>
                <div className="md:col-span-5">
                  <div className="rounded-2xl border border-primary/10 bg-card p-8">
                    <p className="flex items-start gap-3 font-serif text-xl font-medium italic tracking-tight">
                      <ShieldQuestion className="mt-1 h-5 w-5 shrink-0 text-accent" /> {s.q}
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-foreground/80">{s.a}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-24 rounded-3xl bg-primary p-10 text-primary-foreground md:p-16" data-testid="how-cta-band">
            <div className="flex flex-wrap items-center justify-between gap-8">
              <div>
                <h2 className="max-w-xl font-serif text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                  The first session is just a conversation. No commitment beyond one sitting.
                </h2>
                <p className="mt-4 text-sm text-primary-foreground/70">
                  From ₹99 · reschedule free anytime over WhatsApp
                </p>
              </div>
              <Link
                to="/find"
                data-testid="how-cta-find"
                className="group flex items-center gap-2 rounded-full bg-primary-foreground px-8 py-4 text-sm font-bold text-primary transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                Find a therapist
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <BotanicalArt variant="sprout" dark className="w-full max-w-xs" />
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
